// TypeScript declarations for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready?: () => void;
        initData?: string;
        initDataUnsafe?: any;
        colorScheme?: 'light' | 'dark';
        themeParams?: any;
        isExpanded?: boolean;
        viewportHeight?: number;
        viewportStableHeight?: number;
        headerColor?: string;
        backgroundColor?: string;
        BackButton?: {
          isVisible: boolean;
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
        };
        MainButton?: {
          text: string;
          color: string;
          textColor: string;
          isVisible: boolean;
          isActive: boolean;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          onClick: (callback: () => void) => void;
        };
        HapticFeedback?: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
      };
    };
  }
}

/**
 * Check if the app is running inside Telegram
 * The most reliable way is to check for the Telegram WebApp SDK object
 * which is automatically injected by Telegram when the app runs inside it
 */
export const isTelegramWebApp = (): boolean => {
  // Telegram WebApp SDK script is loaded in index.html, so window.Telegram.WebApp
  // exists even in browser. We need to check for initData to confirm we're in Telegram.
  const webApp = window.Telegram?.WebApp;
  return Boolean(webApp && (webApp.initData));
};

/**
 * Get Telegram init data if available
 */
export const getTelegramInitData = (): string | null => {
  return window.Telegram?.WebApp?.initData || null;
};

/**
 * Get Telegram user data if available
 */
export const getTelegramUser = (): any => {
  return window.Telegram?.WebApp?.initDataUnsafe?.user || null;
};

/**
 * Are we running as the Telegram mini-app?
 * True either when the build is flagged as the mini-app (REACT_APP_IS_MINIAPP),
 * or when we detect the Telegram WebApp runtime (site opened inside Telegram).
 * The same source powers both the website and the mini-app; this switch drives
 * the Telegram-only behaviour (auto-auth, native buttons).
 */
export const isMiniApp = (): boolean => {
  return process.env.REACT_APP_IS_MINIAPP === 'true' || isTelegramWebApp();
};

/**
 * Notify Telegram the app is ready and expand it to full height.
 * Safe no-op outside Telegram.
 */
export const initTelegram = (): void => {
  const webApp = window.Telegram?.WebApp as any;
  if (!webApp) return;
  try {
    webApp.ready?.();
    webApp.expand?.();
  } catch {
    /* ignore — older Telegram clients may miss some methods */
  }
};

/**
 * initData to authenticate with. In Telegram we use the real signed string.
 * In local dev (no Telegram runtime) we fall back to REACT_APP_TEST_INIT_DATA so
 * the mini-app flow can be exercised in a browser. Never used in production builds.
 */
export const getInitDataForAuth = (): string | null => {
  const real = getTelegramInitData();
  if (real) return real;
  if (process.env.NODE_ENV !== 'production') {
    return process.env.REACT_APP_TEST_INIT_DATA?.trim() || null;
  }
  return null;
};
